<template>
  <a-config-provider>
    <!-- 지원 탭 배너: 모든 router-view 분기 위, 닫기 없음(계약 SUPPORT 5) -->
    <SupportBanner v-if="!isSupportPage" />
    <router-view v-if="shouldHideSidenav" />
    <div v-else class="app-container">
      <Sidenav class="sidenav" />
      <main class="main-content">
        <router-view />
      </main>
      <!-- 2차 인증 step-up 창: API 가 MFA_REQUIRED·SEND_AUTH_REQUIRED 를 주면 뜬다(W6 B1) -->
      <StepUpModal />
      <TwoFactorPromptModal
        :visible="showTwoFactorPrompt"
        @go-setup="handleGoSetup"
        @dismiss="showTwoFactorPrompt = false"
        @snooze="showTwoFactorPrompt = false"
      />
    </div>
    <!-- 직원 권한 정기 확인(6개월): 로그인 뒤 화면에서 탭·계정마다 한 번, 로그아웃하면 표시를 지움(T-CONTRACT-1031 §2) -->
    <PermReviewModal :active="!shouldHideSidenav && !isSupportPage" />
  </a-config-provider>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { RouterEnum, RouterEnumType } from '@/router/router.enums';
import Sidenav from '@/views/components/Sidenav/index.vue';
import TwoFactorPromptModal, {
  shouldShowTwoFactorPrompt,
} from '@/views/components/TwoFactorPromptModal.vue';
import { get2FAStatus } from '@/views/TwoFactorAuth/api';
import StepUpModal from '@/views/components/StepUpModal.vue';
import apiInstance from '@/axios';
import { factorsOf } from '@/utils/mfa';
import SupportBanner from '@/views/components/SupportBanner.vue';
import PermReviewModal from '@/views/components/PermReviewModal.vue';
import { isSupportTab } from '@/utils/tokenStore';
import { isSupportPagePath } from '@/utils/supportSession';

export default defineComponent({
  name: 'App',
  components: {
    Sidenav,
    TwoFactorPromptModal,
    StepUpModal,
    SupportBanner,
    PermReviewModal,
  },
  computed: {
    isSupportPage(): boolean {
      return isSupportPagePath(this.path);
    },
    shouldHideSidenav(): boolean {
      const hideSidenavRoutes: string[] = [
        RouterEnum.SupportEnter.path,
        RouterEnum.SupportEnded.path,
        RouterEnum.Signin.path,
        RouterEnum.Signin.SetIdAndPw.path,
        RouterEnum.TwoFactorAuth.Verify.path,
        RouterEnum.CallbotUsageFee.UsageStatement.path,
      ];
      return hideSidenavRoutes.some((path: string) =>
        this.path.startsWith(path)
      );
    },
  },
  watch: {
    $route(newRoute) {
      this.path = newRoute.path.toLowerCase();
    },
    shouldHideSidenav(hidden: boolean) {
      if (!hidden) {
        this.checkTwoFactorPrompt();
      }
    },
  },
  data() {
    return {
      RouterEnum: RouterEnum as RouterEnumType,
      path: this.$route.path as string,
      showTwoFactorPrompt: false,
    };
  },
  mounted() {
    if (!this.shouldHideSidenav) {
      this.checkTwoFactorPrompt();
    }
  },
  methods: {
    async checkTwoFactorPrompt() {
      // 지원 계정은 2차 인증을 등록하지 않음(SUPPORT 4)
      if (isSupportTab() || !shouldShowTwoFactorPrompt()) return;
      try {
        // 옛 이메일 OTP·패스키도, 새 휴대폰·이메일 수단(프로필 mfa)도 없을 때만 안내
        const [{ data }, { data: profile }] = await Promise.all([
          get2FAStatus(),
          apiInstance.get('/auth/profile', { showErrorNotice: false }),
        ]);
        if (!data.enabled && !factorsOf(profile?.mfa).methods.length) {
          this.showTwoFactorPrompt = true;
        }
      } catch {
        // 인증 안 된 상태 등 — 무시
      }
    },
    handleGoSetup() {
      this.showTwoFactorPrompt = false;
      // 배포 날은 main 과 같은 옛 2단계 인증 화면으로(새 수단은 「내 계정 보안」 메뉴에서)
      this.$router.push(RouterEnum.TwoFactorAuth.Settings.path);
    },
  },
});
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    'Helvetica Neue', Arial, sans-serif;
  background-color: #f5f5f5;
}

.app-container {
  display: flex;
  min-height: 100vh;
}

.sidenav {
  width: 220px;
  flex-shrink: 0;
}

.main-content {
  flex: 1;
  padding: 24px;
  margin-left: 220px;
  min-height: 100vh;
  background-color: #f5f5f5;
}

/* admin-certilife ant-design.scss 의 .security-modal 중 이 앱에 필요한 것(복사한 2차 인증 창들) */
.security-modal .modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}
.modal-title {
  font-size: 16px;
  font-weight: bold;
}
</style>
